import { useState, useEffect, useCallback, Fragment } from 'react';
import { useSelector } from 'react-redux';
import OverallCompletion from '../components/OverallCompletion';
import CompletionBreakdown from '../components/CompletionBreakdown';
import ExpandableGeneric from '../components/ExpandableGeneric';
import ExpandableGenericDynamic from '../components/ExpandableGeneric/ExpandableGenericDynamic';
import StandardsDashboard from '../components/StandardsDashboard';
import {
  getSecondaryCompletion,
  getNumModules,
  getNumCompleteModules,
  getUnitCompletion,
  getAllApplicableStandards,
  getTotalCompletedStandards,
} from './completionHelpers';
import { getELAStandardsMap } from '../BackendInterface';
import { selectClassroom } from '../Classrooms';
import { RootState } from '../ApplicationState';
import styles from './CurriculumMiddleSchoolDashboard.module.scss';
import { ELAStandardsMap } from '../../peekapak-types/LessonPlanTypes';

const CurriculumMiddleSchoolDashboard = (): JSX.Element => {
  const [isCurriculumExpanded, setIsCurriculumExpanded] = useState(false);
  const [isStandardsExpanded, setIsStandardsExpanded] = useState(false);
  const [standardsMap, setStandardsMap] = useState<ELAStandardsMap>();
  const [isLoaded, setIsLoaded] = useState(false);
  const [resizeTrigger, triggerResize] = useState(false);
  const classroom = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom;
  });
  const completionStatus = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom?.completionStatus;
  });

  const grade = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom?.grade;
  });

  const expandUnits = () => {
    setIsCurriculumExpanded((prevState) => !prevState);
  };
  const expandStandards = () => {
    setIsStandardsExpanded((prevState) => !prevState);
  };
  const handleResize = () => {
    triggerResize((prevState) => !prevState);
  };

  const secondaryCompletion = completionStatus && getSecondaryCompletion(completionStatus, grade);

  const totalCurriculumModules = getNumModules(grade);

  const totalCompleteModules = secondaryCompletion && getNumCompleteModules(secondaryCompletion);

  const secondaryUnitCompletion = secondaryCompletion && getUnitCompletion(secondaryCompletion, grade);

  const allApplicableStandards = standardsMap && getAllApplicableStandards(standardsMap);

  const uniqueStandards = allApplicableStandards && [...new Set(allApplicableStandards)];

  const totalStandards = uniqueStandards?.length || 1;

  const totalCompleteStandards =
    secondaryCompletion && standardsMap && getTotalCompletedStandards(secondaryCompletion, standardsMap, grade);

  const getStandardsData = useCallback(async () => {
    const standardsFromServer = await getELAStandardsMap(grade);
    setStandardsMap(standardsFromServer);
  }, [grade]);

  useEffect(() => {
    secondaryCompletion && setIsLoaded(true);
    grade && getStandardsData();
  }, [classroom]);

  return (
    <div className={styles.container}>
      <div className={styles.titleBar}>
        <h2 className={styles.reportTitle}>Curriculum Completion</h2>
      </div>
      <div className={styles.dataContainer}>
        {!isLoaded ? null : isLoaded && (!secondaryCompletion || Object.keys(secondaryCompletion).length === 0) ? (
          <div className={styles.noData}>
            This class does not have any completion data. Report will be generated as lessons are completed.
          </div>
        ) : (
          <Fragment>
            <OverallCompletion
              title='Curriculum'
              handleClick={expandUnits}
              numComplete={totalCompleteModules || 0}
              totalNum={totalCurriculumModules}
              textButton={`${isCurriculumExpanded ? 'Hide' : 'Show'} Unit Detail`}
              barColor='#012a73'
              isExpanded={isCurriculumExpanded}
            />

            <ExpandableGeneric isExpanded={isCurriculumExpanded}>
              {secondaryUnitCompletion && (
                <CompletionBreakdown
                  colA='Unit Name'
                  colB='Unit Progress'
                  colC='Completion'
                  data={secondaryUnitCompletion}
                  barColor='#012a73'
                  isShouldAnimate={isCurriculumExpanded}
                />
              )}
            </ExpandableGeneric>
            <OverallCompletion
              numComplete={totalCompleteStandards || 0}
              totalNum={totalStandards}
              title='ELA Standards'
              handleClick={expandStandards}
              textButton={`${isStandardsExpanded ? 'Hide' : 'Show'} Detail`}
              barColor='#012a73'
              isExpanded={isStandardsExpanded}
            />
            <ExpandableGenericDynamic isExpanded={isStandardsExpanded} resizeTrigger={resizeTrigger}>
              {standardsMap && secondaryCompletion && classroom && (
                <StandardsDashboard
                  standardsMap={standardsMap}
                  completionStatus={secondaryCompletion}
                  handleResize={handleResize}
                  classroom={classroom}
                  grade={grade}
                />
              )}
            </ExpandableGenericDynamic>
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default CurriculumMiddleSchoolDashboard;
